// ─── Import Validation Constants ────────────────────────────────────────────
const VALID_INVITER_TYPES = ['standard', 'sponsor', 'speaker'];
const MAX_BATCH_SIZE = 100;
const MAX_DISPLAY_NAME_LENGTH = 100;
const MAX_EXTERNAL_ID_LENGTH = 128;
const MAX_ORG_NAME_LENGTH = 200;
const MAX_SPEAKER_NAME_LENGTH = 100;
const MAX_TITLE_LENGTH = 200;
const MAX_TALK_TITLE_LENGTH = 200;
const MAX_URL_LENGTH = 2048;
// ─── Import Item Validators ─────────────────────────────────────────────────
/**
 * Validates a single import inviter item.
 * Checks required fields by type, field length limits, and type enum.
 */
export function validateImportInviterItem(item, index) {
    const errors = [];
    // type enum check
    const type = item.type;
    if (!type || !VALID_INVITER_TYPES.includes(type)) {
        errors.push({
            field: "type",
            message: `type 必须为 ${VALID_INVITER_TYPES.join(" | ")}`,
        });
    }
    // displayName — required for all types
    const displayName = item.displayName;
    if (!displayName || displayName.trim().length === 0) {
        errors.push({ field: "displayName", message: "displayName 不能为空" });
    }
    else if (displayName.length > MAX_DISPLAY_NAME_LENGTH) {
        errors.push({
            field: "displayName",
            message: `displayName 不能超过 ${MAX_DISPLAY_NAME_LENGTH} 字符`,
        });
    }
    // externalId — required for all types
    const externalId = item.externalId;
    if (!externalId || externalId.trim().length === 0) {
        errors.push({ field: "externalId", message: "externalId 不能为空" });
    }
    else if (externalId.length > MAX_EXTERNAL_ID_LENGTH) {
        errors.push({
            field: "externalId",
            message: `externalId 不能超过 ${MAX_EXTERNAL_ID_LENGTH} 字符`,
        });
    }
    // Type-specific validation
    if (type === "sponsor") {
        validateSponsorFields(item, errors);
    }
    else if (type === "speaker") {
        validateSpeakerFields(item, errors);
    }
    return { ok: errors.length === 0, index, errors };
}
function validateSponsorFields(item, errors) {
    const orgName = item.orgName;
    if (!orgName || orgName.trim().length === 0) {
        errors.push({ field: "orgName", message: "sponsor 类型必须提供 orgName" });
    }
    else if (orgName.length > MAX_ORG_NAME_LENGTH) {
        errors.push({
            field: "orgName",
            message: `orgName 不能超过 ${MAX_ORG_NAME_LENGTH} 字符`,
        });
    }
    const logoUrl = item.logoUrl;
    if (logoUrl && logoUrl.length > MAX_URL_LENGTH) {
        errors.push({
            field: "logoUrl",
            message: `logoUrl 不能超过 ${MAX_URL_LENGTH} 字符`,
        });
    }
}
function validateSpeakerFields(item, errors) {
    const speakerName = item.speakerName;
    if (!speakerName || speakerName.trim().length === 0) {
        errors.push({
            field: "speakerName",
            message: "speaker 类型必须提供 speakerName",
        });
    }
    else if (speakerName.length > MAX_SPEAKER_NAME_LENGTH) {
        errors.push({
            field: "speakerName",
            message: `speakerName 不能超过 ${MAX_SPEAKER_NAME_LENGTH} 字符`,
        });
    }
    const talkTitle = item.talkTitle;
    if (!talkTitle || talkTitle.trim().length === 0) {
        errors.push({
            field: "talkTitle",
            message: "speaker 类型必须提供 talkTitle",
        });
    }
    else if (talkTitle.length > MAX_TALK_TITLE_LENGTH) {
        errors.push({
            field: "talkTitle",
            message: `talkTitle 不能超过 ${MAX_TALK_TITLE_LENGTH} 字符`,
        });
    }
    const title = item.title;
    if (title && title.length > MAX_TITLE_LENGTH) {
        errors.push({
            field: "title",
            message: `title 不能超过 ${MAX_TITLE_LENGTH} 字符`,
        });
    }
    const photoUrl = item.photoUrl;
    if (photoUrl && photoUrl.length > MAX_URL_LENGTH) {
        errors.push({
            field: "photoUrl",
            message: `photoUrl 不能超过 ${MAX_URL_LENGTH} 字符`,
        });
    }
}
/**
 * Validates a batch import request.
 * Checks batch size limit (max 100) and validates each item.
 */
export function validateImportInvitersRequest(body) {
    const items = body.items;
    if (!items || !Array.isArray(items)) {
        return {
            ok: false,
            itemErrors: [
                {
                    ok: false,
                    index: -1,
                    errors: [{ field: "items", message: "items 必须为非空数组" }],
                },
            ],
        };
    }
    if (items.length === 0) {
        return {
            ok: false,
            itemErrors: [
                {
                    ok: false,
                    index: -1,
                    errors: [{ field: "items", message: "items 不能为空" }],
                },
            ],
        };
    }
    if (items.length > MAX_BATCH_SIZE) {
        return {
            ok: false,
            itemErrors: [
                {
                    ok: false,
                    index: -1,
                    errors: [
                        {
                            field: "items",
                            message: `批量导入上限 ${MAX_BATCH_SIZE} 条，当前 ${items.length} 条`,
                        },
                    ],
                },
            ],
        };
    }
    const itemErrors = [];
    for (let i = 0; i < items.length; i++) {
        const result = validateImportInviterItem(items[i], i);
        if (!result.ok) {
            itemErrors.push(result);
        }
    }
    return {
        ok: itemErrors.length === 0,
        itemErrors,
    };
}
// ─── Field Validators ───────────────────────────────────────────────────────
/**
 * Returns true if the display name is non-empty after trimming.
 */
export function validateDisplayName(s) {
    return s.trim().length > 0;
}
/**
 * Returns true if the string matches the email pattern: local@domain.tld
 * Pattern: ^[^\s@]+@[^\s@]+\.[^\s@]+$
 */
export function validateEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}
/**
 * Returns true if the string matches the allowed phone character set
 * and is non-empty after trimming.
 * Pattern: ^\+?[0-9 \-()]+$
 */
export function validatePhone(s) {
    if (s.trim().length === 0)
        return false;
    return /^\+?[0-9 \-()]+$/.test(s);
}
/**
 * Returns true if the string is non-empty after trimming.
 */
export function validateRequired(s) {
    return s.trim().length > 0;
}
// ─── Payload Normalization & Validation ─────────────────────────────────────
const REQUIRED_FIELDS = [
    "name",
    "phone",
    "email",
    "company",
    "position",
    "city",
];
/**
 * Trims leading/trailing whitespace from each required field in the payload.
 */
export function normalizeRegistrationPayload(p) {
    return {
        name: p.name.trim(),
        phone: p.phone.trim(),
        email: p.email.trim(),
        company: p.company.trim(),
        position: p.position.trim(),
        city: p.city.trim(),
    };
}
/**
 * Validates a registration payload. Returns { ok: true, fields: [] } when all
 * fields pass, or { ok: false, fields: [...failingFieldNames] } otherwise.
 *
 * Checks:
 * - All 6 fields are non-empty after trim
 * - Email matches the specified format
 * - Phone matches the allowed character set
 */
export function validateRegistrationPayload(p) {
    const failing = [];
    for (const field of REQUIRED_FIELDS) {
        const value = p[field];
        // Check required (non-empty after trim)
        if (!validateRequired(value)) {
            failing.push(field);
            continue;
        }
        // Additional format checks for specific fields
        if (field === "email" && !validateEmail(value)) {
            failing.push(field);
        }
        else if (field === "phone" && !validatePhone(value)) {
            failing.push(field);
        }
    }
    return {
        ok: failing.length === 0,
        fields: failing,
    };
}
//# sourceMappingURL=validators.js.map